import {Color} from '@benchling/nucleus-ui';
import {ActionIcon, createStyles, Group, Text, useMantineTheme} from '@mantine/core';
import React, {useEffect, useRef, useState} from 'react';
import {Node, NodeProps, NodeToolbar, Position, useUpdateNodeInternals} from 'reactflow';

import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {BenchlingNode} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNode';
import {NODE_HOVER_BOX_SHADOW} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';
import {COMMON_NODE_WIDTH} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';

import type {ConnectionRole} from './procedureRunDesignFlowchartConnectionUtils';
import {useProcedureRunDesignFlowchartIsEditable} from './procedureRunDesignFlowchartEditableContext';
import {useProcedureRunDesignFlowchartNodeActions} from './procedureRunDesignFlowchartNodeActionsContext';
import {
  getSourceHandleAffordance,
  ProcedureRunDesignFlowchartNodeHandle,
  ProcedureRunDesignFlowchartNodeHandleVariant,
  ProcedureRunDesignFlowchartNodeHoverGutters,
} from './ProcedureRunDesignFlowchartNodeHandle';
import {getProcedureRunDesignFlowchartNodeId} from './ProcedureRunDesignFlowchartNodeIdUtils';
import type {UnitOperationStatusType} from './procedureRunOverviewUtils';
import {getStatusHeaderColor, getStatusIcon} from './unitOperationStatusVisuals';

export const PROCEDURE_RUN_DESIGN_FLOWCHART_NODE_TYPE = 'procedureRunDesignFlowchartNode';

// Gap (px) between the node's top edge and the NodeToolbar. Must be kept in sync with the
// topHoverGutter top / height so the hover bridge covers the gap exactly.
const NODE_TOOLBAR_OFFSET_PX = 2;

/** Matches COMMON_NODE_WIDTH used by BenchlingNode, which is set as the node's rendered width. */
export const FALLBACK_NODE_WIDTH = COMMON_NODE_WIDTH;
/** Estimated height of a node before ReactFlow measures it. Approximates a node with no description. */
export const FALLBACK_NODE_HEIGHT = 32;
/** The node fields that depend on a cell's archive state. */

export interface CellPresentationFields {
  isMethodHovered: boolean;
  isHighlighted: boolean;
  // True when this specific node is hovered. isMethodHovered tracks hover state for the entire method column. This
  // per-node hover uses the same ReactFlow onNodeMouseEnter/onNodeMouseLeave events as isMethodHovered, so it
  // stays in sync.
  isNodeHovered: boolean;
  // True when this node's condition is persisted and read-only (e.g. editing an existing procedure run).
  // Drives the lock icon in the node header.
  isLocked: boolean;
  /** True when this cell (PMEI) is archived (shown only when "Show archived" is on). */
  isArchived?: boolean;
  /** Human-readable archive reason from `archiveData.purpose`, shown under the title when archived. */
  archiveReason?: string;
  // Leave undefined when statuses should not be rendered
  status?: UnitOperationStatusType;
  // This node's role in the upstream->active draw affordance. Derived from the methods and active layer.
  connectionRole: ConnectionRole;
  // Whether this node's source handle should stay visible at rest (not just on hover). Edge-dependent,
  // only meaningful when connectionRole === 'source'; a source node always shows its handle on hover.
  showPersistentSourceHandle: boolean;
}

export interface ProcedureRunDesignFlowchartNodeData extends CellPresentationFields {
  conditionName: string;
  replicateNumber: number;
  conditionDescription?: string;
  methodId: string;
  // Defaults to 'subtle' (transparent) when omitted.
  handleVariant?: ProcedureRunDesignFlowchartNodeHandleVariant;
  isInActiveMethod: boolean;
  isPendingRemoval: boolean;
  isRemoved: boolean;
}

export function createProcedureRunDesignFlowchartNode({
  conditionIndex,
  replicateIndex,
  methodId,
  conditionName,
  replicateNumber,
  conditionDescription,
  isArchived,
  archiveReason,
  isMethodHovered,
  isHighlighted,
  isLocked,
  status,
  connectionRole = null,
  showPersistentSourceHandle = false,
  isInActiveMethod,
}: {
  conditionIndex: number;
  replicateIndex: number;
  methodId: string;
  conditionName: string;
  replicateNumber: number;
  conditionDescription?: string;
  isArchived?: boolean;
  archiveReason?: string;
  isMethodHovered: boolean;
  isHighlighted: boolean;
  isLocked: boolean;
  status?: UnitOperationStatusType;
  connectionRole?: ConnectionRole;
  showPersistentSourceHandle?: boolean;
  isInActiveMethod: boolean;
}): Node<ProcedureRunDesignFlowchartNodeData> {
  return {
    id: getProcedureRunDesignFlowchartNodeId({conditionIndex, replicateIndex, methodId}),
    type: PROCEDURE_RUN_DESIGN_FLOWCHART_NODE_TYPE,
    data: {
      conditionName,
      replicateNumber,
      conditionDescription,
      methodId,
      isArchived,
      archiveReason,
      isMethodHovered,
      isHighlighted,
      isNodeHovered: false,
      isLocked,
      status,
      connectionRole,
      showPersistentSourceHandle,
      isInActiveMethod,
      isPendingRemoval: false,
      isRemoved: false,
    },
    position: {
      x: 0,
      y: 0,
    },
    selectable: false,
    deletable: false,
    draggable: false,
    connectable: false,
  };
}

export function isProcedureRunDesignFlowchartNode(
  node: Node
): node is Node<ProcedureRunDesignFlowchartNodeData> {
  return node.type === PROCEDURE_RUN_DESIGN_FLOWCHART_NODE_TYPE;
}

export default function ProcedureRunDesignFlowchartNode(
  props: NodeProps<ProcedureRunDesignFlowchartNodeData>
): JSX.Element {
  const {classes, cx} = useStyles();
  const theme = useMantineTheme();
  const {data, id} = props;
  const {
    isMethodHovered,
    isHighlighted,
    isNodeHovered,
    isLocked,
    status,
    connectionRole,
    showPersistentSourceHandle,
    isInActiveMethod,
    isPendingRemoval,
    isRemoved,
    isArchived = false,
    archiveReason,
  } = data;
  const sourceAffordance = getSourceHandleAffordance(status);

  // The active-layer handles mount based on the node's role and edit mode but float without changing
  // the node's measured size, so ReactFlow won't re-measure on its own. Refresh the handle geometry
  // whenever that set changes, otherwise edges and the drag preview line can't anchor to the new handle.
  const isEditable = useProcedureRunDesignFlowchartIsEditable();
  const updateNodeInternals = useUpdateNodeInternals();
  // Edit mode only flips the rendered handle set for source/target nodes. Null-role nodes always render
  // just the flush handle, so keep their key constant to avoid needless re-measures on edit mode toggle.
  const handleConfigKey = connectionRole == null ? 'null' : `${connectionRole}:${isEditable}`;
  const prevHandleConfigKeyRef = useRef(handleConfigKey);
  useEffect(() => {
    if (prevHandleConfigKeyRef.current !== handleConfigKey) {
      updateNodeInternals(id);
      prevHandleConfigKeyRef.current = handleConfigKey;
    }
  }, [handleConfigKey, id, updateNodeInternals]);

  const {onHoverTrashIcon, onUnhoverTrashIcon, onClickTrashIcon} =
    useProcedureRunDesignFlowchartNodeActions();
  // Tracks whether the cursor is on the NodeToolbar itself. NodeToolbar renders via a ReactFlow
  // portal outside the node's DOM wrapper, so ReactFlow fires onNodeMouseLeave when the cursor moves
  // from the node into the toolbar. This state keeps the toolbar visible while the cursor is on it.
  const [isNodeToolbarHovered, setIsNodeToolbarHovered] = useState(false);
  // Archived cells are inert and can't be removed, so they never surface the remove-replicate toolbar.
  const shouldShowNodeToolbar =
    isEditable && (isNodeHovered || isNodeToolbarHovered) && isInActiveMethod && !isRemoved && !isArchived;
  const isHoveredOrHighlighted = !isRemoved && (isMethodHovered || isHighlighted);
  // Archived cells are inert: they render greyed out and ignore hover/highlight and status tinting.
  // Otherwise hover/highlight tints the whole node while the status color tints only the header.
  const rootBg = isArchived
    ? theme.colors.gray[1]
    : isHoveredOrHighlighted
      ? theme.colors.blue[0]
      : theme.white;
  const headerBg = isArchived
    ? theme.colors.gray[1]
    : isHoveredOrHighlighted
      ? theme.colors.blue[0]
      : getStatusHeaderColor(status ?? null);
  const {icon: statusIcon, iconColor: statusIconColor} = getStatusIcon(status ?? null);

  const handleTrashIconClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    onClickTrashIcon(id);
  };

  return (
    <>
      <NodeToolbar
        isVisible={shouldShowNodeToolbar}
        position={Position.Top}
        offset={NODE_TOOLBAR_OFFSET_PX}
        onMouseEnter={() => {
          setIsNodeToolbarHovered(true);
          onHoverTrashIcon(id);
        }}
        onMouseLeave={() => {
          setIsNodeToolbarHovered(false);
          onUnhoverTrashIcon();
        }}
      >
        <ActionIcon
          size="xs"
          variant="filled"
          color="dark"
          radius="md"
          aria-label="Remove condition replicate"
          onClick={handleTrashIconClick}
          className={classes.trashButton}
          {...getTestProps({component: 'ProcedureRunDesignFlowchartNode', element: 'trashIconButton'})}
        >
          <BenchlingIcon icon="trash-can" className={classes.trashIcon} />
        </ActionIcon>
      </NodeToolbar>
      <BenchlingNode
        isSelectable={!isArchived}
        isSelected={!isArchived && !isRemoved && !isPendingRemoval && isHighlighted}
        w="200px"
        data-pending-removal={isPendingRemoval && !isRemoved ? true : undefined}
        sx={(theme) => ({
          backgroundColor: rootBg,
          ...(isPendingRemoval && !isRemoved && {borderColor: theme.colors.red[8]}),
          ...(isMethodHovered && !isRemoved && !isArchived && {boxShadow: NODE_HOVER_BOX_SHADOW}),
          ...(isRemoved && {visibility: 'hidden', pointerEvents: 'none'}),
        })}
        dataTestAttrs={{
          element: 'root',
          key: props.id,
        }}
      >
        {!isRemoved && (
          <>
            <ProcedureRunDesignFlowchartNodeHoverGutters />
            {/* Bridges the gap between the node top edge and the NodeToolbar so the cursor can travel
              to the node toolbar without onNodeMouseLeave firing and hiding it. */}
            {shouldShowNodeToolbar && <span aria-hidden={true} className={classes.topHoverGutter} />}
            {/* Each slot delegates to the flush handle or the active-layer connection handle by role. */}
            <ProcedureRunDesignFlowchartNodeHandle
              nodeId={id}
              handleType="target"
              connectionRole={connectionRole}
              showPersistentSourceHandle={false}
              isNodeHovered={isNodeHovered}
              variant={data.handleVariant}
            />
            <ProcedureRunDesignFlowchartNodeHandle
              nodeId={id}
              handleType="source"
              connectionRole={connectionRole}
              showPersistentSourceHandle={showPersistentSourceHandle}
              isNodeHovered={isNodeHovered}
              sourceAffordance={sourceAffordance}
              variant={data.handleVariant}
            />
          </>
        )}
        <BenchlingNode.Header
          bg={headerBg}
          {...getTestProps({component: 'ProcedureRunDesignFlowchartNode', element: 'header'})}
        >
          <Group spacing="sm" align="flex-start" noWrap={true}>
            <Group spacing={theme.spacing.xs} align="center" noWrap={true} className={classes.nameAndLock}>
              {isLocked && (
                <BenchlingIcon
                  icon="lock"
                  className={classes.lockIcon}
                  {...getTestProps({component: 'ProcedureRunDesignFlowchartNode', element: 'lockIcon'})}
                />
              )}
              <Text className={cx(classes.conditionReplicate, isArchived && classes.archivedText)}>
                {`${data.conditionName} - ${data.replicateNumber}${isArchived ? ' (archived)' : ''}`}
              </Text>
            </Group>
            {statusIcon && (
              <BenchlingIcon
                icon={statusIcon}
                className={classes.statusIcon}
                style={{color: statusIconColor}}
              />
            )}
          </Group>
          {data.conditionDescription ? (
            <Text className={classes.description}>{data.conditionDescription}</Text>
          ) : null}
          {isArchived && archiveReason ? (
            <Text
              className={classes.archiveReason}
              {...getTestProps({component: 'ProcedureRunDesignFlowchartNode', element: 'archiveReason'})}
            >
              {archiveReason}
            </Text>
          ) : null}
        </BenchlingNode.Header>
        {/* TODO (BNCH-153699): Include sections with varied parameters */}
      </BenchlingNode>
    </>
  );
}

const useStyles = createStyles((theme) => ({
  // Transparent region above the node that bridges the gap between the node top edge and the
  // NodeToolbar, so the cursor can move to the toolbar without triggering onNodeMouseLeave and hiding it.
  topHoverGutter: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: -NODE_TOOLBAR_OFFSET_PX,
    height: NODE_TOOLBAR_OFFSET_PX,
    pointerEvents: 'auto',
  },
  trashButton: {
    '&:hover': {
      backgroundColor: theme.colors.red[8],
    },
  },
  trashIcon: {
    color: theme.white,
  },
  nameAndLock: {
    flex: 1,
    minWidth: 0,
  },
  conditionReplicate: {
    flex: 1,
    minWidth: 0,
    fontWeight: 600,
    fontSize: theme.fontSizes.sm,
    lineHeight: '16px',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  description: {
    color: Color.GRAY_600,
    fontSize: theme.fontSizes.xxs,
    lineHeight: '14px',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  archivedText: {
    color: Color.GRAY_600,
  },
  archiveReason: {
    color: Color.GRAY_600,
    fontStyle: 'italic',
    fontSize: theme.fontSizes.xxs,
    lineHeight: '14px',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  statusIcon: {
    width: 16,
    height: 16,
    flexShrink: 0,
  },
  lockIcon: {
    width: 8,
    height: 8,
    flexShrink: 0,
    color: Color.GRAY_600,
  },
}));
